/**
 * UploadFilesDialog
 *
 * Modal that hosts the multi-file upload UI for the File Manager:
 * - drag-and-drop area
 * - file picker
 * - selected file list with per-row remove
 * - upload / cancel actions
 *
 * State (selected files, isDragging, current folder context) is owned by
 * the parent page; this component is purely presentational + emits
 * callbacks. Keeps Enter-to-submit and the existing button labels.
 */

import { type FileItem } from "@crm-src/lib/files-api"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Upload, FileText, Trash2 } from "lucide-react"

interface UploadFilesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentFolder: FileItem | null
  uploadFiles: File[]
  isDragging: boolean
  onDragEnter: (e: React.DragEvent) => void
  onDragLeave: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (index: number) => void
  onUpload: () => void
  onCancel: () => void
}

export function UploadFilesDialog({
  open,
  onOpenChange,
  currentFolder,
  uploadFiles,
  isDragging,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileInputChange,
  onRemoveFile,
  onUpload,
  onCancel,
}: UploadFilesDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && uploadFiles.length > 0) {
            e.preventDefault()
            onUpload()
          }
        },
      }}
    >
      <DialogTitle>Upload Files</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select files to upload to {currentFolder ? `"${currentFolder.name}"` : 'the root folder'}.
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Drag and Drop Area */}
          <Box
            sx={{
              border: 2,
              borderStyle: "dashed",
              borderRadius: 1,
              p: 4,
              textAlign: "center",
              transition: "border-color .2s, background-color .2s",
              borderColor: isDragging ? "primary.main" : "divider",
              bgcolor: isDragging ? (theme) => alpha(theme.palette.primary.main, 0.08) : "transparent",
              "&:hover": isDragging ? undefined : { borderColor: "text.disabled" },
            }}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2, color: isDragging ? "primary.main" : "text.disabled" }}>
              <Upload size={48} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 500, color: isDragging ? "primary.main" : "text.primary" }}>
                {isDragging ? 'Drop files here' : 'Drag and drop files here'}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>or</Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button variant="contained" component="label" htmlFor="files" sx={{ cursor: "pointer" }}>
                  Choose Files
                  <input
                    id="files"
                    type="file"
                    multiple
                    onChange={onFileInputChange}
                    style={{
                      position: "absolute",
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: "hidden",
                      clip: "rect(0 0 0 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  />
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Selected Files List */}
          {uploadFiles.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Selected Files ({uploadFiles.length})</Typography>
              <Box sx={{ maxHeight: 128, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.5 }}>
                {uploadFiles.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      bgcolor: "surfaceSoft",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      <Box component="span" sx={{ color: "text.secondary", display: "inline-flex", flexShrink: 0 }}>
                        <FileText size={16} />
                      </Box>
                      <Typography variant="body2" noWrap title={file.name}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                        ({file.size < 1024 * 1024
                          ? `${(file.size / 1024).toFixed(1)} KB`
                          : `${(file.size / 1024 / 1024).toFixed(1)} MB`
                        })
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveFile(index)}
                      sx={{ flexShrink: 0, "&:hover": { bgcolor: (theme) => alpha(theme.palette.error.main, 0.12), color: "error.main" } }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onUpload} disabled={uploadFiles.length === 0}>
          Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
